import { useState, useEffect, useRef, useCallback } from "react";
import { request } from "../services/api";

const MAX_FAILURES = 5;
const SCAN_INTERVAL_MS = 5000;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const STALE_CONNECTION_MS = 45000;

const getReconnectDelay = (attempt) => {
  const jitter = Math.floor(Math.random() * 700);
  return (
    Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * Math.pow(2, attempt)) +
    jitter
  );
};

const isBrowserOffline = () => {
  return typeof navigator !== "undefined" && navigator.onLine === false;
};

export const useMonitorSocket = (deviceId) => {
  const [device, setDevice] = useState(null);
  const [sensorData, setSensorData] = useState({});
  const [wsConnected, setWsConnected] = useState(false);
  const [suspendedSensors, setSuspendedSensors] = useState(new Set());

  const failedAttempts = useRef({});
  const wsRef = useRef(null);
  const deviceRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const staleCheckIntervalRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const lastMessageAtRef = useRef(0);
  const isMountedRef = useRef(false);
  const manualCloseRef = useRef(false);
  const connectSocketRef = useRef(null);

  const getSocketUrl = useCallback(() => {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";

    if (isLocalhost) {
      return "wss://sfarm.tabangohar.com/ws";
    }

    return `wss://${hostname}/ws`;
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearSocketTimers = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    if (staleCheckIntervalRef.current) {
      clearInterval(staleCheckIntervalRef.current);
      staleCheckIntervalRef.current = null;
    }
  }, []);

  const closeSocket = useCallback(() => {
    clearSocketTimers();

    const ws = wsRef.current;

    if (
      ws &&
      (ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING)
    ) {
      try {
        ws.close();
      } catch (error) {
        console.error("Monitor socket close error:", error);
      }
    }

    wsRef.current = null;
  }, [clearSocketTimers]);

  const parseDetailedReport = useCallback((rawText, type, index) => {
    const key = `${type}${index}`;
    const isDisconnected = rawText.includes("Disconnected");

    if (isDisconnected) {
      failedAttempts.current[key] = (failedAttempts.current[key] || 0) + 1;

      if (failedAttempts.current[key] >= MAX_FAILURES) {
        setSuspendedSensors((prev) => {
          const newSet = new Set(prev);
          newSet.add(key);
          return newSet;
        });
      }
    } else {
      failedAttempts.current[key] = 0;

      setSuspendedSensors((prev) => {
        if (prev.has(key)) {
          const newSet = new Set(prev);
          newSet.delete(key);
          return newSet;
        }

        return prev;
      });
    }

    const statusMatch = rawText.match(
      /status:\s*(\d+)(?:\s*\(By:\s*([^)]+)\))?/,
    );
    const statusValue = statusMatch ? statusMatch[1] : null;
    const causedByValue = statusMatch && statusMatch[2] ? statusMatch[2] : "";

    let errorValue = "";

    if (rawText.includes("_____Error_____")) {
      const errorMatch = rawText.match(/\(Is depended on ([^)]+)\)/);
      errorValue = errorMatch ? errorMatch[1] : "Dependent";
    }

    let currentValue = "--";
    const valMatch = rawText.match(/(temperature|humidity|wind):\s*([\d\.]+)/i);

    if (valMatch) {
      currentValue = valMatch[2];
    }

    const trigMatch = rawText.match(/(trig|callTrig|cDTrig):\s*(\d)/i);
    const isTriggered = trigMatch ? trigMatch[2] === "1" : false;

    const extract = (prop) => {
      const singleTypes = ["wind", "secu", "gsm", "remote"];
      const safeIndex = index !== undefined && index !== null ? index : 0;
      const prefix = singleTypes.includes(type) ? type : `${type}${safeIndex}`;

      const regex = new RegExp(`${prefix}\\.${prop}=([\\d\\w\\-:,/\\.]+)`, "i");
      const match = rawText.match(regex);

      return match ? match[1] : "";
    };

    setSensorData((prev) => ({
      ...prev,
      [key]: {
        connected: !isDisconnected,
        current: currentValue,
        status: statusValue,
        causedBy: causedByValue,
        error: errorValue,
        triggered: isTriggered,
        settings: {
          enable: extract("enable"),
          alertSms: extract("alertSms"),
          toDOut: extract("toDOut"),
          reverse: extract("reverse"),
          setpoint: extract("setpoint"),
          hyster: extract("hyster"),
          leadDelay: extract("leadDelay"),
          lagDelay: extract("lagDelay"),
          logicMode: extract("logicMode"),
          timerMode: extract("timerMode"),
          delayOn: extract("delayOn"),
          delayOff: extract("delayOff"),
          periodicOn: extract("periodicOn"),
          periodicOff: extract("periodicOff"),
          dailyOn: extract("dailyOn"),
          dailyOff: extract("dailyOff"),
          zone: extract("zone"),
          armDelay: extract("armDelay"),
          reactDelay: extract("reactDelay"),
          timeout: extract("timeout"),
          learnedNumber: extract("learnedNumber"),
          callEnableSecu: extract("callEnableSecu"),
          callToDOut: extract("callToDOut"),
          learnedRemote: extract("learnedRemote"),
          cDToDOut: extract("cDToDOut"),
        },
      },
    }));
  }, []);

  const requestScan = useCallback((forceAll = false) => {
    const ws = wsRef.current;
    const currentDevice = deviceRef.current;

    if (!ws || ws.readyState !== WebSocket.OPEN || !currentDevice) return;

    const targets = [];
    const shouldScan = (key) =>
      forceAll ||
      !failedAttempts.current[key] ||
      failedAttempts.current[key] < MAX_FAILURES;

    ["temp", "humi"].forEach((type) => {
      for (let i = 0; i < 9; i++) {
        if (shouldScan(`${type}${i}`)) targets.push(`${type}${i}`);
      }
    });

    if (shouldScan("wind0")) targets.push("wind");
    if (shouldScan("secu0")) targets.push("secu");
    if (shouldScan("gsm0")) targets.push("gsm");
    if (shouldScan("remote0")) targets.push("remote");

    for (let i = 0; i < 68; i++) {
      if (shouldScan(`dIn${i}`)) targets.push(`dIn${i}`);
    }

    for (let i = 0; i < 72; i++) {
      if (shouldScan(`dOut${i}`)) targets.push(`dOut${i}`);
    }

    if (targets.length === 0) return;

    try {
      ws.send(
        JSON.stringify({
          action: "scan",
          serial: currentDevice.device_serial,
          targets,
        }),
      );
    } catch (error) {
      console.error("Monitor scan send error:", error);
      setWsConnected(false);

      try {
        ws.close();
      } catch (closeError) {
        console.error("Monitor scan close error:", closeError);
      }
    }
  }, []);

  const scheduleReconnect = useCallback(
    (immediate = false) => {
      if (!isMountedRef.current || manualCloseRef.current || !deviceRef.current)
        return;

      clearReconnectTimer();

      const delay = immediate
        ? 0
        : getReconnectDelay(reconnectAttemptRef.current);

      reconnectTimerRef.current = setTimeout(() => {
        if (!isMountedRef.current || manualCloseRef.current) return;

        reconnectAttemptRef.current += 1;

        if (connectSocketRef.current) {
          connectSocketRef.current();
        }
      }, delay);
    },
    [clearReconnectTimer],
  );

  const connectSocket = useCallback(() => {
    const currentDevice = deviceRef.current;

    if (!currentDevice || !isMountedRef.current) return;

    if (isBrowserOffline()) {
      setWsConnected(false);
      scheduleReconnect(false);
      return;
    }

    manualCloseRef.current = false;
    clearReconnectTimer();
    closeSocket();

    const wsUrl = getSocketUrl();
    console.log(`Connecting to: ${wsUrl}`);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    lastMessageAtRef.current = Date.now();

    ws.onopen = () => {
      if (!isMountedRef.current || wsRef.current !== ws) return;

      console.log("Monitor Socket Connected");
      setWsConnected(true);
      reconnectAttemptRef.current = 0;
      lastMessageAtRef.current = Date.now();

      try {
        ws.send(
          JSON.stringify({
            action: "monitor",
            serial: currentDevice.device_serial,
          }),
        );

        requestScan(true);
      } catch (error) {
        console.error("Monitor registration error:", error);
        ws.close();
        return;
      }

      clearSocketTimers();

      scanIntervalRef.current = setInterval(() => {
        if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) return;
        requestScan(false);
      }, SCAN_INTERVAL_MS);

      staleCheckIntervalRef.current = setInterval(() => {
        if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) return;

        const elapsed = Date.now() - lastMessageAtRef.current;

        if (elapsed > STALE_CONNECTION_MS) {
          console.warn("Monitor socket stale. Reconnecting...");

          try {
            ws.close();
          } catch (error) {
            console.error("Monitor stale close error:", error);
          }
        }
      }, 10000);
    };

    ws.onmessage = (event) => {
      if (!isMountedRef.current || wsRef.current !== ws) return;

      lastMessageAtRef.current = Date.now();
      setWsConnected(true);
      reconnectAttemptRef.current = 0;

      let data;

      try {
        data = JSON.parse(event.data);
      } catch (error) {
        console.error("Monitor socket JSON parse error:", error);
        return;
      }

      if (Array.isArray(data)) {
        data.forEach((item) => {
          if (item?.raw) {
            parseDetailedReport(item.raw, item.type, item.index);
          }
        });
        return;
      }

      if (data?.raw) {
        parseDetailedReport(data.raw, data.type, data.index);
      }
    };

    ws.onerror = () => {
      if (!isMountedRef.current || wsRef.current !== ws) return;

      setWsConnected(false);

      try {
        ws.close();
      } catch (error) {
        console.error("Monitor socket error close failed:", error);
      }
    };

    ws.onclose = () => {
      if (!isMountedRef.current || wsRef.current !== ws) return;

      clearSocketTimers();
      wsRef.current = null;
      setWsConnected(false);
      console.log("Socket Disconnected");
      scheduleReconnect(false);
    };
  }, [
    clearReconnectTimer,
    clearSocketTimers,
    closeSocket,
    getSocketUrl,
    parseDetailedReport,
    requestScan,
    scheduleReconnect,
  ]);

  connectSocketRef.current = connectSocket;

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    let attempt = 0;

    const getDeviceInfo = async () => {
      if (!deviceId) return;

      try {
        const res = await request("/get_devices.php");

        if (cancelled) return;

        if (res.status === "success") {
          const target = (res.devices || []).find(
            (d) => String(d.id) === String(deviceId),
          );

          if (target) {
            setDevice(target);
            attempt = 0;
            return;
          }
        }

        throw new Error(res?.message || "Device not found");
      } catch (err) {
        if (cancelled) return;

        console.error("API Error:", err);

        const delay = getReconnectDelay(attempt);
        attempt += 1;

        retryTimer = setTimeout(getDeviceInfo, delay);
      }
    };

    const handleOnline = () => {
      attempt = 0;

      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }

      getDeviceInfo();
    };

    window.addEventListener("online", handleOnline);

    getDeviceInfo();

    return () => {
      cancelled = true;
      window.removeEventListener("online", handleOnline);

      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [deviceId]);

  useEffect(() => {
    deviceRef.current = device || null;
  }, [device]);

  useEffect(() => {
    if (!device) return undefined;

    isMountedRef.current = true;
    manualCloseRef.current = false;
    reconnectAttemptRef.current = 0;
    lastMessageAtRef.current = Date.now();
    setWsConnected(false);

    connectSocket();

    const handleOnline = () => {
      reconnectAttemptRef.current = 0;
      scheduleReconnect(true);
    };

    const handleOffline = () => {
      setWsConnected(false);
      closeSocket();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      isMountedRef.current = false;
      manualCloseRef.current = true;

      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);

      clearReconnectTimer();
      closeSocket();
    };
  }, [
    device,
    connectSocket,
    scheduleReconnect,
    closeSocket,
    clearReconnectTimer,
  ]);

  const sendCommand = useCallback(
    (type, index, prop, value) => {
      const ws = wsRef.current;
      const currentDevice = deviceRef.current;

      if (!ws || ws.readyState !== WebSocket.OPEN || !currentDevice) {
        setWsConnected(false);
        scheduleReconnect(true);
        return false;
      }

      const singleTypes = ["wind", "secu", "gsm", "remote"];
      const prefix = singleTypes.includes(type) ? type : `${type}${index}`;

      const cmd = `${prefix}.${prop}=${value}`;
      console.log("Sending MQTT Command:", cmd);

      try {
        ws.send(
          JSON.stringify({
            action: "command",
            serial: currentDevice.device_serial,
            command: cmd,
          }),
        );
      } catch (error) {
        console.error("Monitor command send error:", error);
        setWsConnected(false);

        try {
          ws.close();
        } catch (closeError) {
          console.error("Monitor command close error:", closeError);
        }

        return false;
      }

      const key = `${type}${index}`;

      setSensorData((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          settings: {
            ...prev[key]?.settings,
            [prop]: value,
          },
        },
      }));

      return true;
    },
    [scheduleReconnect],
  );

  const handleRetryAll = useCallback(() => {
    failedAttempts.current = {};
    setSuspendedSensors(new Set());

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      requestScan(true);
      return;
    }

    reconnectAttemptRef.current = 0;
    scheduleReconnect(true);
  }, [requestScan, scheduleReconnect]);

  return {
    device,
    sensorData,
    wsConnected,
    suspendedSensors,
    sendCommand,
    handleRetryAll,
  };
};
